import cx from 'classnames'
import React, {
  FC,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import { Icons } from 'src/components/Icon'
import { FormLabelProps } from 'src/form/FormLabel'
import { FormFieldOrientationValues } from 'src/types/form'

import * as SC from './styled'

export type SelectOption = {
  label?: string
  value?: string
}

export type FormFieldSelectProps = InputHTMLAttributes<HTMLSelectElement> & {
  autoWidth?: boolean
  name: string
  className?: string
  error?: string
  errors?: { message?: string }[]
  helpText?: string
  innerError?: boolean
  label?: string
  labelProps?: FormLabelProps
  required?: boolean
  options?: SelectOption[]
  orientation?: FormFieldOrientationValues
  placeholderDisabled?: boolean
}

const FormFieldSelect: FC<FormFieldSelectProps> = ({
  autoWidth = false,
  className,
  value,
  name,
  id: propId,
  error,
  innerError,
  helpText,
  placeholder,
  onBlur,
  onChange,
  label,
  labelProps,
  options,
  orientation,
  placeholderDisabled = true,
  ...inputProps
}) => {
  const localId = useId()
  const id = propId || localId
  const [isFocused, setFocused] = useState(false)
  const isActive = isFocused || value !== ''
  const hasLabel = !!label

  const refSelect = useRef<HTMLSelectElement>(null)
  const refGhost = useRef<HTMLSpanElement>(null)
  const [selectWidth, setSelectWidth] = useState<number | null>(null)

  const updateSelectWidth = useCallback(() => {
    if (!refSelect.current || !refGhost.current || !autoWidth) return

    const options = refSelect.current.options
    const selectedOption = options[refSelect.current.selectedIndex]
    const text = selectedOption?.text ?? placeholder ?? ''

    refGhost.current.textContent = text
    const ghostWidth = refGhost.current.offsetWidth

    setSelectWidth(ghostWidth)
  }, [autoWidth, placeholder])

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    onBlur?.(e)
    setFocused(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange?.(e)
    setFocused(false)
    updateSelectWidth()
  }

  useEffect(() => {
    updateSelectWidth()

    let resizeObserver: ResizeObserver | null = null
    const selectEl = refSelect.current

    if (typeof ResizeObserver !== 'undefined' && selectEl && autoWidth) {
      resizeObserver = new ResizeObserver(() => {
        updateSelectWidth()
      })
      resizeObserver.observe(selectEl)
    }

    return () => {
      if (resizeObserver && selectEl) {
        resizeObserver.unobserve(selectEl)
      }
      resizeObserver?.disconnect()
    }
  }, [autoWidth, updateSelectWidth])

  return (
    <SC.Container
      className={cx('FormFieldSelect', className)}
      $orientation={orientation}
      style={{ width: selectWidth ? `${selectWidth}px` : undefined }}
    >
      {label && (
        <SC.Label underlined {...labelProps}>
          {label}
        </SC.Label>
      )}
      <SC.MainWrapper>
        <SC.Ghost ref={refGhost} aria-hidden={true} />
        <SC.InputContainer
          className={cx('FormFieldSelectInputContainer', className)}
          $isError={!!error}
          $isActive={isActive}
          $isDisabled={inputProps.disabled}
          $hasLabel={hasLabel}
          htmlFor={id}
        >
          <SC.DropDown icon={Icons.chevronDown} $isActive={isActive} />
          <SC.Input
            {...inputProps}
            {...(id && { id })}
            value={value}
            name={name}
            placeholder={placeholder}
            ref={refSelect}
            $isError={!!error}
            $isActive={isActive}
            $hasLabel={hasLabel}
            $isDisabled={inputProps.disabled}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
          >
            <option
              value={''}
              {...(placeholderDisabled && { disabled: true })}
              defaultChecked
            >
              {placeholder || label}
            </option>
            {options?.map((option, index) => (
              <option value={option.value} key={index}>
                {option.label}
              </option>
            ))}
          </SC.Input>
        </SC.InputContainer>
        {error && innerError && <SC.ErrorMessage>{error}</SC.ErrorMessage>}
      </SC.MainWrapper>
      {!error && helpText && <SC.HelpMessage>{helpText}</SC.HelpMessage>}
      {error && !innerError && <SC.ErrorMessage>{error}</SC.ErrorMessage>}
    </SC.Container>
  )
}

export default FormFieldSelect
